import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, db } from "../firebase";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  deleteUser,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  sendPasswordResetEmail,
} from "firebase/auth";
import { collection, doc, getDoc, getDocs, limit, query, serverTimestamp, setDoc, where } from "firebase/firestore";

const AuthContext = createContext();
const PRIMARY_ADMIN_EMAIL = "studiozula148@gmail.com";

const normalizeEmail = (value) => (value || "").trim().toLowerCase();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const googleProvider = new GoogleAuthProvider();

  const getUserRef = (userId) => doc(db, "users", userId);

  const hydrateUser = async (firebaseUser, allowPrimaryBootstrap = false, options = {}) => {
    const allowSelfRegistration = options.allowSelfRegistration === true;
    const profileName = (options.profileName || "").trim();
    const email = normalizeEmail(firebaseUser?.email);
    const isPrimaryAdmin = email === PRIMARY_ADMIN_EMAIL;
    const displayName = (firebaseUser?.displayName || "").trim();
    const phoneNumber = (firebaseUser?.phoneNumber || "").trim();
    const authUid = firebaseUser?.uid;

    if (!authUid) {
      const err = new Error("משתמש לא תקין.");
      err.code = "auth/invalid-user";
      throw err;
    }

    let userId = email || authUid;
    let userRef = getUserRef(userId);
    let userSnap = await getDoc(userRef);
    let data = userSnap.exists() ? userSnap.data() : null;

    if (!data && email && userId !== authUid) {
      userId = authUid;
      userRef = getUserRef(userId);
      userSnap = await getDoc(userRef);
      data = userSnap.exists() ? userSnap.data() : null;
    }

    if (!data && allowPrimaryBootstrap && isPrimaryAdmin) {
      data = {
        name: displayName || "מנהל מערכת",
        email,
        role: "coordinator",
        isSuperAdmin: true,
        active: true,
        volunteerId: null,
        createdAt: serverTimestamp(),
      };
      await setDoc(userRef, data);
    }

    if (!data && allowSelfRegistration) {
      data = {
        name: profileName || displayName || "משתמש חדש",
        email: email || "",
        phone: phoneNumber || "",
        role: "volunteer",
        isSuperAdmin: false,
        active: true,
        volunteerId: null,
        authUid,
        createdAt: serverTimestamp(),
      };
      await setDoc(userRef, data);
    }

    if (!data) {
      const err = new Error("החשבון לא מוגדר במערכת. פנה לרכז להוספה.");
      err.code = "auth/not-authorized";
      throw err;
    }

    if (isPrimaryAdmin && (data.role !== "coordinator" || !data.active || data.isSuperAdmin !== true)) {
      await setDoc(userRef, { role: "coordinator", active: true, isSuperAdmin: true }, { merge: true });
      data = { ...data, role: "coordinator", active: true, isSuperAdmin: true };
    }

    if (!data.active) {
      const err = new Error("החשבון קיים אך אינו פעיל. פנה לרכז להפעלה.");
      err.code = "auth/user-disabled";
      throw err;
    }

    if (data.role === "volunteer" && !data.volunteerId && email) {
      let matchingVolunteers = await getDocs(
        query(collection(db, "volunteers"), where("email", "==", email), limit(2))
      );
      let matchedVolunteerId = matchingVolunteers.size === 1 ? matchingVolunteers.docs[0].id : null;

      if (!matchedVolunteerId && matchingVolunteers.empty) {
        const allVolunteers = await getDocs(collection(db, "volunteers"));
        const byLowerEmail = allVolunteers.docs.filter((d) => {
          const vEmail = (d.data().email || "").trim().toLowerCase();
          return vEmail && vEmail === email;
        });
        if (byLowerEmail.length === 1) {
          matchedVolunteerId = byLowerEmail[0].id;
        }
      }

      if (matchedVolunteerId) {
        await setDoc(userRef, { volunteerId: matchedVolunteerId }, { merge: true });
        data = { ...data, volunteerId: matchedVolunteerId };
      }
    }

    const patch = {
      authUid,
      lastLoginAt: serverTimestamp(),
    };
    if (!data.name && displayName) {
      patch.name = displayName;
      data = { ...data, name: displayName };
    }
    if (!data.email && email) {
      patch.email = email;
      data = { ...data, email };
    }
    if (!data.phone && phoneNumber) {
      patch.phone = phoneNumber;
      data = { ...data, phone: phoneNumber };
    }
    await setDoc(userRef, patch, { merge: true });

    return { id: userId, ...data, isSuperAdmin: data?.isSuperAdmin === true || isPrimaryAdmin, authUid };
  };

  async function loginWithGoogle() {
    const cred = await signInWithPopup(auth, googleProvider);
    try {
      const resolvedUser = await hydrateUser(cred.user, true);
      setUserData(resolvedUser);
      return cred;
    } catch (err) {
      await signOut(auth);
      throw err;
    }
  }

  async function loginWithEmail(email, password) {
    const cred = await signInWithEmailAndPassword(auth, normalizeEmail(email), password);
    try {
      const resolvedUser = await hydrateUser(cred.user, true);
      setUserData(resolvedUser);
      return cred;
    } catch (err) {
      await signOut(auth);
      throw err;
    }
  }

  async function registerWithEmail(email, password, profileName = "") {
    const normalized = normalizeEmail(email);
    const cred = await createUserWithEmailAndPassword(auth, normalized, password);
    try {
      const resolvedUser = await hydrateUser(cred.user, false, {
        allowSelfRegistration: true,
        profileName,
      });
      setUserData(resolvedUser);
      return cred;
    } catch (err) {
      try {
        await deleteUser(cred.user);
      } catch (deleteErr) {
        console.error("Failed to rollback unauthorized signup:", deleteErr);
      }
      await signOut(auth);
      throw err;
    }
  }

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, normalizeEmail(email));
  }

  async function updateCurrentUserName(nextName) {
    const name = (nextName || "").trim();
    if (!name) {
      throw new Error("יש להזין שם.");
    }
    if (!userData?.id) {
      throw new Error("לא נמצא משתמש מחובר לעדכון.");
    }
    await setDoc(getUserRef(userData.id), { name, updatedAt: serverTimestamp() }, { merge: true });
    setUserData((prev) => (prev ? { ...prev, name } : prev));
  }

  function logout() {
    setUserData(null);
    return signOut(auth);
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const resolvedUser = await hydrateUser(user, true);
          setUserData(resolvedUser);
        } catch (e) {
          console.error("Error loading user data:", e);
          await signOut(auth);
          setUserData(null);
        }
      } else {
        setUserData(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const value = {
    currentUser,
    userData,
    loginWithGoogle,
    loginWithEmail,
    registerWithEmail,
    resetPassword,
    updateCurrentUserName,
    logout,
    loading,
    // Transitional support: legacy `group_coordinator` keeps coordinator access until migration completes.
    isCoordinator: userData?.role === "coordinator" || userData?.role === "group_coordinator",
    isGroupCoordinator: false,
    isVolunteer: userData?.role === "volunteer",
    isSuperAdmin: userData?.isSuperAdmin === true,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
